/**
 * Asset attribute data
 * This model represents a single record of an asset's attribute datum
 * If the attribute data is for a true multivalue attribute, `this.valuesCollection` will be populated with a record for each value in the `values` array
 * `this.valuesCollection` is just an attributes collection with amlagamated attribute models (read: monster)
 * @class
 */
import Base from '@netx/core/lib/netx-model';

import AttributesCollection from '../collections/attributes';
import {getPreference} from '@netx/core-user-next/utils/getPreference';

export default class AttributeData extends Base {
	/** @type {string} */
	get idAttribute() {
		return 'templateId';
	}

	/**
	 * @property {object}  defaults             - The default attribute values
	 * @property {string}  defaults.templateId  - ID
	 * @property {number}  defaults.assetId     - Asset id (redundant)
	 * @property {string}  defaults.permission  - Permission (d/r/w)
	 * @property {array}   defaults.values      - Attribute values
	 */
	get defaults() {
		return {
			templateId: 0,
			assetId: 0,
			permission: 'r',
			values: [],

			// Client
			autofilled: false,
		};
	}

	/** @type {array} */
	get clientAttrs() {
		return ['autofilled'];
	}

	/**
	 * @override
	 * @param {array} models An array of models or model attributes
	 * @param {object} opts Collection options
	 * @listens module:system#system:attribute:edit:success
	 */
	constructor(models, opts = {}) {
		// Circular reference fix
		// attributes collection requires asset attribute datum collection which
		// is the top level parent of this model
		// const AttributesCollection = require('../collections/attributes').default;

		super(models, opts);

		/**
		 * @memberof Netx.Models.AttributeData
		 * @alias assetAttributes
		 * @type {module:asset/collections/attributes}
		 */
		this.assetAttributes = opts.assetAttributes || Netx.attributes;

		/**
		 * Will be used as a one off for our multivalue childViews and data
		 * @memberof Netx.Models.AttributeData
		 * @alias valuesCollection
		 * @type {Netx.Collections.AssetAttributes}
		 */
		this.valuesCollection = new AttributesCollection([]);

		this.metadataLookupEnabled = getPreference(
			'category.metadataDatasetLookupEnabled',
			true,
		);

		// Hey, listen!
		this.listenTo(this, 'change:values', () => {
			this.populateValuesCollection();
		});

		// Wait for initialize to fully complete
		_.defer(_.bind(this.populateValuesCollection, this));

		/**
		 * Store original value
		 * @memberof module:asset/models/attributeData
		 * @type {mixed}
		 */
		this.originalValue = this.getValue();

		// Hey, listen!
		this.listenTo(dispatcher, 'system:attribute:edit:success', (model) => {
			if (model.id === this.id && model !== this) {
				this.set(
					_.clone(_.omit(model.attributes, 'attributeid', 'templateid')),
				);
			}
		})
			// Always have one empty value in last place
			.listenTo(
				this.valuesCollection,
				'change',
				_.debounce(() => {
					const lastModel = _.last(this.valuesCollection.models);
					const value = (lastModel && lastModel.getValue()) || [];

					if (!lastModel || (value.length && value[0] !== '')) {
						this.addValue();
					}
				}, 50),
			);
	}

	/**
	 * @method
	 * @param {mixed} value Value to add
	 */
	addValue(value) {
		if (!this.get('multivalue')) {
			return;
		}

		// Make a safe copy so we do not pollute original data
		const data = this.createValueModelAttributes(value);

		// Always set to an empty value in this.attributes.values (and quietly so we do not trigger repopulation)
		// since the values collection models will cache their current values
		this.set('values', this.get('values').concat(''), {silent: true});

		this.valuesCollection.add(data);
	}

	/**
	 * @method
	 * @param {string} perm Permission string ( "delete", "write/(edit)", "share", etc )
	 * @return {boolean} Allowed
	 */
	allows(perm) {
		if (perm == null) {
			return undefined;
		}

		const template = this.getTemplate();
		if (!template.isValueWritable()) {
			return false;
		}

		switch (perm) {
			case 'delete':
				return this.canManage();
			case 'edit':
			case 'write':
				return this.canEdit();
			default:
				return undefined;
		}
	}

	/**
	 * @method
	 * @return {boolean}
	 */
	canEdit() {
		return (
			this.get('readOnly') !== true &&
			(this.get('permission') === 'w' || this.canManage())
		);
	}

	/**
	 * @method
	 * @return {boolean}
	 */
	canManage() {
		return !this.has('permission') || this.get('permission') === 'd';
	}

	/**
	 * @method
	 */
	clearChanges() {
		this.changed = {};
		this.originalValue = this.getValue();
		return this;
	}

	/**
	 * @method
	 * @param {mixed} value Value
	 * @return {object}
	 */
	createValueModelAttributes(value) {
		if (_.isUndefined(value) || _.isNull(value)) {
			value = [''];
		}
		if (!_.isArray(value)) {
			value = [value];
		}
		return _.extend({}, _.clone(this.self()), {
			// This is purely for the sake of having a unique id, this will not be used in any other way
			attributeid: _.uniqueId('attribute-value-'),
			values: value,
		});
	}

	/** @override */
	getLabel() {
		const template = this.getTemplate();
		return template && template.getLabel();
	}

	/**
	 * @method
	 * @return {module:asset/models/assetAttribute}
	 */
	getTemplate() {
		return this.assetAttributes.get(this.id);
	}

	/**
	 * @method
	 * @return {string}
	 */
	getTypeName() {
		const template = this.getTemplate();
		return template && template.getTypeName();
	}

	/**
	 * @method
	 * @return {array|mixed} Value(s)
	 */
	getValue() {
		// Is not a multivalue type attribute
		if (!this.get('multivalue')) {
			return this.get('values')[0] || '';
		}

		return this.get('values');
	}

	/**
	 * @method
	 * @return {boolean}
	 */
	hasValue() {
		const valuesCollection = this.valuesCollection;

		for (let i = 0, c = valuesCollection.length, vals; i < c; i++) {
			vals = valuesCollection.at(i).get('values');
			if (vals.length && vals[0] !== '') {
				return true;
			}
		}
		return false;
	}

	/**
	 * @method
	 * @param {mixed} value Value
	 * @return {boolean}
	 */
	hasValueChanged(value) {
		value = value != null ? value : this.get('values');

		let originalValue = this.get('multiple') ? '' : this.originalValue;

		if (!_.isArray(originalValue)) {
			value = value !== '' ? [value] : [];
		}

		if (!_.isArray(originalValue)) {
			originalValue = originalValue !== '' ? [originalValue] : [];
		}
		return value.join() !== originalValue.join();
	}

	/**
	 * Try to perform an auto-complete of attributes (DAM-6749)
	 * @method
	 */
	metadataLookup() {
		const attrTemplate = this.getTemplate();
		if (!attrTemplate.get('keyDatasets').length) return;

		attrTemplate
			.metadataDatasetLookup(this.getValue(), {
				duplicateQueryPolicy: 'abort',
			})
			.then((matches) => {
				this.trigger('metadataLookup:complete', this, _.compact(matches));
			});
	}

	/**
	 * @method
	 * @param {boolean} useOriginalValues Use original values
	 * @param {boolean} dontMerge Do not merge the models
	 */
	populateValuesCollection(useOriginalValues, dontMerge) {
		const values = useOriginalValues ? this.originalValue : this.get('values');
		// Map out our `values` array to objects for model creation
		const data = _.map(values, (value) =>
			this.createValueModelAttributes(value),
		);

		// Always have at least one empty at the end
		data.push(this.createValueModelAttributes());

		this.valuesCollection.set(data, {merge: dontMerge !== true});
	}

	/**
	 * @method
	 * @param {string} id Value id
	 */
	removeValue(id) {
		if (!this.get('multivalue')) {
			return;
		}

		const valuesCollection = this.valuesCollection;
		const valueModel = valuesCollection.get(id);
		const values = this.get('values');

		if (valueModel) {
			const idx = _.indexOf(values, valueModel.get('value')[0]);
			if (idx > -1) {
				values.splice(idx, 1, '');
				// Remove quietly so we do not trigger repopulation
				this.set('value', values, {silent: true});
			}
		}

		valuesCollection.remove(id);

		// Always have at least one empty field
		const count = valuesCollection.length;
		if (!count) {
			this.addValue();
		} else {
			const vals = valuesCollection.at(count - 1).get('values');
			if (!vals.length || vals[0] !== '') {
				this.addValue();
			}
		}
	}

	/**
	 * @method
	 * @param {object} opts Options
	 */
	resetValues(opts) {
		const values = this.originalValue;
		this.setValue(values, opts);
		this.clearChanges();
	}

	/**
	 * @method
	 * @param {mixed} value Value
	 * @param {object} opts Options
	 */
	setValue(value, opts = {}) {
		// Must already exist and have permission to edit
		if (!this.canEdit()) {
			return;
		}

		// Undefined/null/empty string are all considered an empty array
		if (_.isUndefined(value) || _.isNull(value) || value === '') {
			value = [];
		}

		// Regardless of "multivalue" value array - all values are dealt with as arrays
		// Not already an array
		// Is an array but is a multivalue value attribute, so there must be at least one value in the array and that value must also be an array
		if (!_.isArray(value)) {
			value = [value];
		}

		// Set asset attribute value(s)
		this.set(
			{
				values: value,
				autofilled: opts.autofilled === true,
			},
			opts,
		);
	}

	/**
	 * @method
	 * @static
	 * @param {module:asset/models/attribute} attrModel Attribute template
	 * @param {object} opts Options
	 */
	static CreateFromTemplate(attrModel, opts = {}) {
		if (!attrModel) {
			throw new Error('Attribute template required');
		}

		return new this(
			_.extend(
				_.omit(
					attrModel.attributes,
					'attributeid',
					'objectTypes',
					'templateid',
					'value',
				),
				{
					templateId: attrModel.id,
					permission: 'r',
				},
				opts,
			),
		);
	}

	/** @type {string} */
	static get __name__() {
		return 'AttributeData';
	}
}
